import { forwardRef, useImperativeHandle } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { useClientCompanyForm } from '../application/useClientCompanyForm';
import type { Id } from '../domain/enterprise.types';
import type { ClientCompany } from '../domain/clientCompany.types';

export interface ClientCompanyFormDialogHandle {
  openCreate: () => void;
  openEdit: (company: ClientCompany) => void;
}

export interface ClientCompanyFormDialogProps {
  clientId: Id;
  onSaved: () => void;
}

// UC-9 (decisión E: literales españoles as-is). Posee su propio `useClientCompanyForm`; el
// contenedor lo abre imperativamente vía ref (openCreate/openEdit), igual que el botón "Nueva
// empresa" y el lápiz de cada fila de `ClientCompaniesTable` no tienen otro punto de contacto
// con el estado interno del diálogo.
const ClientCompanyFormDialog = forwardRef<
  ClientCompanyFormDialogHandle,
  ClientCompanyFormDialogProps
>(function ClientCompanyFormDialog({ clientId, onSaved }, ref) {
  const { open, editing, form, setField, saving, openCreate, openEdit, close, save } =
    useClientCompanyForm(clientId, onSaved);

  useImperativeHandle(ref, () => ({ openCreate, openEdit }), [openCreate, openEdit]);

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle>{editing ? 'Editar empresa' : 'Nueva empresa del grupo'}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid size={{ xs: 12, sm: 8 }}>
            <TextField
              fullWidth
              required
              label="Nombre"
              value={form.name}
              onChange={(e) => setField('name', e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              label="NIT"
              value={form.nit}
              onChange={(e) => setField('nit', e.target.value)}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Descripción"
              value={form.description}
              onChange={(e) => setField('description', e.target.value)}
            />
          </Grid>
          <Grid size={12}>
            <FormControl fullWidth>
              <InputLabel>Estado</InputLabel>
              <Select
                value={form.status}
                label="Estado"
                onChange={(e) => setField('status', e.target.value as ClientCompany['status'])}
              >
                <MenuItem value="active">Activa</MenuItem>
                <MenuItem value="inactive">Inactiva</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Cancelar</Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? 'Guardando...' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

export default ClientCompanyFormDialog;
